import { useNavigate, useSearchParams } from 'react-router-dom';
import ShipmentIcon from 'components/icons/ShipmentIcon';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { ManifestFormFields } from '../types';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useFormikContext } from 'formik';
import { CreateManifestPayload } from 'store/manifests/types';
import useManifests from 'hooks/useManifests';
import Notification from 'components/ui/base/notification/Notification';
import { TransportType } from 'utils/transportTypes';
import useSession from 'hooks/useSession';
import useIndividuals from 'hooks/useIndividuals';
import { Individual } from 'store/individuals/individual';
import { getErrorMessage } from 'utils/getErrorMessage';
import moment from 'moment';

/** Backend expects LocalDateTime (no timezone offset). */
const toLocalDateTime = (value?: string | null) => (value ? moment(value).format('YYYY-MM-DDTHH:mm:ss') : null);

const CreateManifestPageHeader: React.FC = () => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isReadyToSubmit, isSubmitting } = useMultiStepFormContext<ManifestFormFields>();
    const { values } = useFormikContext<ManifestFormFields>();
    const { createManifest } = useManifests();
    const { userInfo } = useSession();
    const { getIndividual } = useIndividuals();

    const teamIdParam = searchParams.get('teamId');

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSubmit = async () => {
        try {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Creating manifest...',
                },
            });

            let teamId: string | undefined = undefined;
            if (values.team.teamId) {
                // If the user is admin, get the team from values
                teamId = values.team.teamId;
            } else if (teamIdParam) {
                teamId = teamIdParam;
            } else {
                // If the user is TEAM MANAGER, inherit their team
                if (!userInfo?.individualId) {
                    throw new Error('Could not get user data');
                }
                const res: Individual = await getIndividual(userInfo?.individualId);
                const activeTeam = res.teamMemberships[0];
                teamId = activeTeam.teamId;
            }

            if (!teamId) {
                throw new Error('Can not create a manifest without a team');
            }

            const {
                carrierOrHaulier,
                transportTypeId,
                countryOfDispatch,
                customsOfficeOfPresentation,
                departureDateTime,
                arrivalDateTime,
                containerIndication,
                meansId,
                meansIdentificationType,
                nationalityTransportMeans,
                vehicleRegistration,
                departurePort,
            } = values.transportDetails;

            if (containerIndication === null || containerIndication === undefined) {
                throw Error('Container Indication is required');
            }

            const payload: CreateManifestPayload = {
                teamId,
                referenceId: values.manifest.referenceId,
                haulier: carrierOrHaulier,
                departurePort,
                declarationsData: {
                    transportId: transportTypeId as TransportType,
                    departureCountryId: countryOfDispatch,
                    arrivalPort: customsOfficeOfPresentation,
                    dateTimeOfDeparture: toLocalDateTime(departureDateTime),
                    estimatedDateTimeOfArrival: moment(arrivalDateTime).format('YYYY-MM-DDTHH:mm:ss'),
                    containerIndicator: containerIndication,
                    meansIdentificationType,
                    meansOfTransportOnArrivalIdNumber: meansId,
                    nationalityTransportMeans,
                    vehicleRegistration,
                },
            };

            const createdManifest = await createManifest(payload);

            if (!createdManifest?.id) {
                throw new Error('Could not create manifest');
            }

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'Manifest Created',
                description: 'The manifest has been created successfully',
            });

            navigate(`/manifests/${createdManifest.id}`, { replace: true });
        } catch (err: any) {
            let errorMessage = getErrorMessage(err);
            const data = err?.response?.data ?? err;

            // Display the be validation errors inside the toast
            if (data?.subErrors && Array.isArray(data.subErrors) && data.subErrors.length > 0) {
                const errors: string[] = [];
                data.subErrors.forEach((subErr: any) => {
                    const message = subErr.message || data.message;
                    errors.push(message);
                });
                errorMessage = errors.join(' ');
            }

            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Manifest Creation Error',
                description: errorMessage || 'Could not create manifest. Please try again.',
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    return (
        <PageHeader
            title="Manifest"
            icon={<ShipmentIcon />}
            sidebar={
                <div>
                    <Button style={{ marginRight: 16 }} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" disabled={!isReadyToSubmit()} loading={isSubmitting} onClick={handleSubmit}>
                        Create Manifest
                    </Button>
                </div>
            }
        />
    );
};

export default CreateManifestPageHeader;
